import { Component, createRef, useMemo, type ReactNode } from "react";
import { Box, motionDurations, motionEasings } from "@hightouch/htui";
import { animate, useReducedMotion } from "motion/react";
import type { AmpLibraryFileFragment } from "src/graphql";
import { getAdAssetKind } from "src/pages/ad-studio/components/ad-asset-kind";
import { whenImageSettles } from "src/pages/projects/library/canvas/image-settle";
import { useCanvasAdPreview } from "src/pages/projects/library/canvas/canvas-ads";

/** The longest the old preview waits for the new one's images before it lets go. */
const CROSSFADE_LOAD_TIMEOUT_MS = 5000;
/** How long the old preview waits for the new content to show an image: ad markup lays out a frame or two late. */
const IMAGE_APPEAR_MS = 150;
/** An image ad renders nothing until its signed URL arrives, so it gets longer to show its image. */
const SIGNED_IMAGE_APPEAR_MS = 1500;

const after = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Resolves once the new content's images have loaded or given up. An ad lays out its markup a frame or two after
 * the swap, so the wait first looks for images; content that shows none by then has nothing left to paint.
 */
async function whenContentSettles(
  root: HTMLElement,
  ghost: HTMLElement,
  imageAppearMs: number,
) {
  const liveImages = () =>
    [...root.querySelectorAll("img")].filter((image) => !ghost.contains(image));
  const giveUpAt = performance.now() + imageAppearMs;
  while (liveImages().length === 0) {
    if (performance.now() >= giveUpAt) return;
    await nextFrame();
  }
  const pending = liveImages().filter((image) => !image.complete);
  if (pending.length === 0) return;
  await Promise.race([
    Promise.all(pending.map(whenImageSettles)),
    after(CROSSFADE_LOAD_TIMEOUT_MS),
  ]);
}

/**
 * Gives the copy's ids their own names, references included, so its gradients, masks and clip paths keep painting
 * while two versions of the markup share the document. Attributes are rewritten in place: rebuilding the markup
 * would reload every image in the copy.
 */
function renameIds(ghost: HTMLElement) {
  const ids = new Set(
    [...ghost.querySelectorAll("[id]")].map((element) => element.id),
  );
  if (ids.size === 0) return;
  const renamed = (value: string) =>
    value.replace(/#([^\s)"']+)/g, (match, id: string) =>
      ids.has(id) ? `#${id}-ghost` : match,
    );
  for (const element of ghost.querySelectorAll("*")) {
    for (const attribute of [...element.attributes]) {
      if (attribute.name === "id") {
        element.setAttribute("id", `${attribute.value}-ghost`);
      } else if (attribute.value.includes("#")) {
        const next = renamed(attribute.value);
        if (next !== attribute.value)
          element.setAttribute(attribute.name, next);
      }
    }
  }
}

type Props = {
  contentKey: string;
  imageAppearMs: number;
  reduceMotion: boolean;
  children: ReactNode;
};
type Snapshot = { ghost: HTMLElement; height: number } | "keep" | null;
type Hold = { ghost: HTMLElement; isFading: boolean; end: () => void };

/**
 * Keeps a copy of the old content over the new until the new one's images have loaded, holding the old height
 * meanwhile, then fades the copy out. A class because only `getSnapshotBeforeUpdate` sees the DOM before React replaces it.
 */
class Crossfade extends Component<Props> {
  private root = createRef<HTMLDivElement>();
  private hold?: Hold;
  private waitId = 0;

  override getSnapshotBeforeUpdate(previous: Props): Snapshot {
    const root = this.root.current;
    if (previous.contentKey === this.props.contentKey || !root) return null;
    // The copy still up is the last content that painted, so it stands in for the newer content too.
    if (this.hold && !this.hold.isFading) return "keep";
    this.hold?.end();
    // A cloned iframe reloads blank, and a cloned canvas or video paints blank, so those swap in place.
    if (root.querySelector("iframe, canvas, video")) return null;
    const ghost = root.cloneNode(true) as HTMLElement;
    ghost.setAttribute("aria-hidden", "true");
    ghost.inert = true;
    renameIds(ghost);
    Object.assign(ghost.style, {
      position: "absolute",
      inset: "0",
      zIndex: "1",
      pointerEvents: "none",
    });
    return { ghost, height: root.offsetHeight };
  }

  override componentDidUpdate(
    _previous: Props,
    _state: unknown,
    snapshot: Snapshot,
  ) {
    const root = this.root.current;
    if (!snapshot || !root) return;
    if (snapshot !== "keep") {
      const { ghost, height } = snapshot;
      root.style.height = `${height}px`;
      root.append(ghost);
      const hold: Hold = {
        ghost,
        isFading: false,
        end: () => {
          if (this.hold !== hold) return;
          this.hold = undefined;
          ghost.remove();
          root.style.height = "";
        },
      };
      this.hold = hold;
    }
    const hold = this.hold;
    if (!hold) return;
    const waitId = ++this.waitId;
    void whenContentSettles(root, hold.ghost, this.props.imageAppearMs).then(
      () => {
        if (this.hold !== hold || waitId !== this.waitId) return;
        hold.isFading = true;
        root.style.height = "";
        if (this.props.reduceMotion) return hold.end();
        void animate(
          hold.ghost,
          { opacity: [1, 0] },
          { duration: motionDurations.normal, ease: motionEasings.easeOutQuad },
        ).then(hold.end, hold.end);
      },
    );
  }

  override componentWillUnmount() {
    this.hold?.end();
  }

  override render() {
    return (
      <Box ref={this.root} position="relative">
        {this.props.children}
      </Box>
    );
  }
}

/** A card's preview that crossfades when the file gets a new version, instead of swapping hard. */
export function ContentCrossfade({
  file,
  children,
}: {
  file: AmpLibraryFileFragment;
  children: ReactNode;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  // An ad's saved asset is its current look, and it lands a poll after the version that made it.
  const { preview, isLoading } = useCanvasAdPreview(file);
  const composition =
    preview && "video_composition" in preview
      ? preview.video_composition
      : null;
  const componentAssets =
    preview && "component_assets" in preview ? preview.component_assets : null;
  // Each poll hands back new objects, so their content is the key, read once per object.
  const compositionKey = useMemo(
    () => (composition ? JSON.stringify(composition) : ""),
    [composition],
  );
  // The image behind an `ad:` reference can change while the markup stays the same.
  const componentAssetsKey = useMemo(
    () => (componentAssets ? JSON.stringify(componentAssets) : ""),
    [componentAssets],
  );
  const adKey = isLoading
    ? "loading"
    : [
        getAdAssetKind(preview),
        preview?.asset_url,
        preview?.svg,
        compositionKey,
        componentAssetsKey,
      ].join("|");
  const contentKey = [
    file.generationStatus,
    file.ad ? adKey : file.versionId,
  ].join("|");
  return (
    <Crossfade
      contentKey={contentKey}
      imageAppearMs={
        file.ad && preview?.asset_url && !preview.svg
          ? SIGNED_IMAGE_APPEAR_MS
          : IMAGE_APPEAR_MS
      }
      reduceMotion={reduceMotion}
    >
      {children}
    </Crossfade>
  );
}
